import {DomainStore, fetchModels} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {
  TimeTrackingPolicy,
  TimeTrackingPaySchedule,
  TimeTrackingPayPeriod
} from 'main/stores/time_tracking';
import {User} from 'main/stores/users';
import _ from 'lodash';
import {TimeOffType} from 'main/stores/time_off';

class PolicyEditFlowState {
  store = new DomainStore();
  match;
  history;

  policy;
  errors = {};
  selectedEmployees = [];
  paySchedules = [];
  selectedPaySchedule;
  newPayPeriod = new TimeTrackingPayPeriod();
  calculatedPayPeriod = new TimeTrackingPayPeriod();
  assignee = {};
  isCreatingNewPaySchedule = false;
  dailyOvertimeEnabled = true;
  weeklyOvertimeEnabled = true;
  doubleOvertimeEnabled = true;
  bankRegularHoursEnabled = false;
  bankOvertimeEnabled = false;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false
    });
  }

  receiveProps({match, history}) {
    this.match = match;
    this.history = history;
  }

  async load() {
    await this.store._compose([
      endpoints.TIME_TRACKING.POLICIES.with(this.match.params.id)
    ]);

    this.policy = new TimeTrackingPolicy(this.store._getSingle(types.TIME_TRACKING.POLICY));
    this.initializeOvertimeFlags();
  }

  async onStepSubmitted({location}) {
    switch (location) {
      case 'policy_details': return this.submitPolicyDetails();
      case 'employees': return this.submitEmployees();
      case 'overtime': return this.submitOvertime();
      case 'lieu_time': return this.submitLieuTime();
      case 'pay_schedule': return this.submitPaySchedule();
      case 'assignee': return this.submitAssignee();
      case 'review': return this.configurePolicy();
      default:
        throw new Error(`Location ${location} is not supported`);
    }
  }

  async submitStep(props) {
    const policy = this.policy.pick(props);
    const {model, errors} = await this.store.patch(policy);

    this.errors = errors;
    if (!model) return false;

    this.policy.update(model);
    return true;
  }

  async onStepChanged({location}) {
    switch (location) {
      case 'pay_schedule':
        await this.initializePaySchedules();
        break;
      case 'employees':
        await this.initializeEmployees();
        break;
      case 'overtime':
      case 'lieu_time':
        await this.initializeTimeOffTypes();
        break;
      case 'assignee':
        await this.initializeAssignees();
        break;
      default:
        break;
    }
  }

  async submitPolicyDetails() {
    return this.submitStep([
      'name',
      'manuallyEnterHours',
      'timeClockEnabled',
      'collectEmployeeLocation',
      'timeClockLocation',
      'geofencingEnabled',
      'shiftSchedulingEnabled'
    ]);
  }

  async submitOvertime() {
    const props = [];

    if (this.dailyOvertimeEnabled) {
      props.push('dailyOvertimeThreshold');
    }

    if (this.weeklyOvertimeEnabled) {
      props.push('weeklyOvertimeThreshold');
    }

    if (this.doubleOvertimeEnabled) {
      props.push('dailyDoubleOvertimeThreshold');
    }

    props.push('overtimePolicy');

    return this.submitStep(props);
  }

  async submitLieuTime() {
    return this.submitStep([
      'lieuTimeEnabled',
      'lieuTimeMultiplier',
      'lieuTimeDoubleMultiplier',
      'lieuTimeWeeklyThreshold',
      'lieuTimePTOType'
    ]);
  }

  async initializeEmployees() {
    this.selectedEmployees = this.policy.employees;
  }

  async initializeTimeOffTypes() {
    await this.store._compose([
      endpoints.TIME_OFF.TYPES.ALL
    ]);
    this.timeOffTypes = this.store._getAll(types.TIME_OFF.TYPE, TimeOffType);
  }

  async initializePaySchedules() {
    await this.store._compose([
      endpoints.TIME_TRACKING.PAY_SCHEDULES.SUMMARY
    ]);
    this.paySchedules = this.store._getAll(types.TIME_TRACKING.PAY_SCHEDULE).map(e => new TimeTrackingPaySchedule(e, this));
    this.selectedPaySchedule = this.policy.paySchedule;
    if (this.policy.weekStartDay == null) this.policy.merge({weekStartDay: 0});
    if (_.isEmpty(this.paySchedules)) {
      this.isCreatingNewPaySchedule = true;
      this.selectedPaySchedule = new TimeTrackingPaySchedule();
    }
  }

  async initializeAssignees() {
    await this.store._compose([
      endpoints.USERS.ACTIVE
    ]);

    const users = this.store._getAll(types.USER).map(u => new User(u));

    this.availableAssignees = [
      {
        id: 'manager', type: 'manager', name: t('time_tracking.manage.policies.edit.Manager')
      },
      ...users.map(u => {
        return { id: u.id, type: 'specific_user', user: u, name: u.name };
      })
    ];

    this.assignee = _.find(this.availableAssignees, a => {
      return (this.policy.assigneeType === 'manager' && a.type === 'manager') ||
        (this.policy.assigneeType === 'specific_user' && a.id === _.get(this.policy, 'assigneeUser.id'));
    });
  }

  async payScheduleSelected(payScheduleId) {
    if (payScheduleId === '-1') {
      this.isCreatingNewPaySchedule = true;
      this.selectedPaySchedule = new TimeTrackingPaySchedule();
      this.newPayPeriod = new TimeTrackingPayPeriod();
    } else {
      this.isCreatingNewPaySchedule = false;
      this.selectedPaySchedule = _.find(this.paySchedules, {id: payScheduleId});
    }
  }

  initializeOvertimeFlags() {
    this.dailyOvertimeEnabled = !!this.policy.dailyOvertimeThreshold;
    this.weeklyOvertimeEnabled = !!this.policy.weeklyOvertimeThreshold;
    this.doubleOvertimeEnabled = !!this.policy.dailyDoubleOvertimeThreshold;
    this.bankOvertimeEnabled = !!this.policy.lieuTimeMultiplier;
    this.bankRegularHoursEnabled = !!this.policy.lieuTimeWeeklyThreshold;
  }

  toggleDailyOvertime(enabled) {
    this.dailyOvertimeEnabled = enabled;
    if (!enabled) {
      this.policy.dailyOvertimeThreshold = null;
      if (this.doubleOvertimeEnabled) this.toggleDoubleOvertime(false);
    } else {
      this.policy.dailyOvertimeThreshold = 8;
    }
  }

  toggleWeeklyOvertime(enabled) {
    this.weeklyOvertimeEnabled = enabled;
    if (!enabled) {
      this.policy.weeklyOvertimeThreshold = null;
    } else {
      this.policy.weeklyOvertimeThreshold = 40;
    }
  }

  toggleDoubleOvertime(enabled) {
    this.doubleOvertimeEnabled = enabled;
    if (!enabled) {
      this.policy.dailyDoubleOvertimeThreshold = null;
    } else {
      this.policy.dailyDoubleOvertimeThreshold = 12;
    }
  }

  get overtimeStandard() {
    return this.policy.overtimePolicy === 'standard';
  }

  updateSelectedEmployees(employees) {
    this.selectedEmployees.replace(employees);
  }

  updateOvertimePolicy(overtimePolicy) {
    this.policy.overtimePolicy = overtimePolicy;

    if (overtimePolicy !== 'custom') {
      this.toggleWeeklyOvertime(false);
      this.toggleDailyOvertime(false);
      this.toggleDoubleOvertime(false);
    }

    if (overtimePolicy === 'exempt') {
      this.toggleBankOvertime(false);
    }
  }

  async submitEmployees() {
    this.policy.employees = this.selectedEmployees;

    return this.submitStep(['employees']);
  }

  selectAssignee(assignee) {
    this.policy.assigneeType = assignee.type;
    this.policy.assigneeUser = assignee.user;
    this.assignee = assignee;
  }

  async submitAssignee() {
    return this.submitStep(['assigneeType', 'assigneeUser']);
  }

  async calculatePayPeriods() {
    if (this.isCreatingNewPaySchedule && this.newPayPeriod.startDate && this.selectedPaySchedule.frequency) {
      [this.newPayPeriod, this.calculatedPayPeriod] = await this.fetchCalculatedPayPeriods();
    }
  }

  setLieuTimeEnabled(enabled) {
    this.policy.merge({lieuTimeEnabled: enabled});

    if (!enabled) {
      this.toggleBankRegularHours(false);
      this.toggleBankOvertime(false);
      this.policy.merge({lieuTimePTOType: null});
    }
  }

  toggleBankRegularHours(enabled) {
    this.bankRegularHoursEnabled = enabled;

    if (enabled) {
      this.policy.merge({lieuTimeWeeklyThreshold: 40});
    } else {
      this.policy.merge({lieuTimeWeeklyThreshold: null});
    }
  }

  toggleBankOvertime(enabled) {
    this.bankOvertimeEnabled = enabled;

    if (enabled) {
      this.policy.merge({lieuTimeMultiplier: 1.5, lieuTimeDoubleMultiplier: 2});
    } else {
      this.policy.merge({lieuTimeMultiplier: null, lieuTimeDoubleMultiplier: null});
    }
  }

  async fetchCalculatedPayPeriods() {
    const periods = await fetchModels(
      endpoints.TIME_TRACKING.PAY_SCHEDULES.CALCULATE_INITIAL_PERIODS.with(
        this.newPayPeriod.startDate.toISOString(),
        this.selectedPaySchedule.frequency
      ),
      types.TIME_TRACKING.PAY_PERIOD
    );
    return _.orderBy(periods.map(model => new TimeTrackingPayPeriod(model)), 'startDate', 'asc');
  }

  async submitPaySchedule() {
    if (this.isCreatingNewPaySchedule) {
      this.selectedPaySchedule.payPeriods = [this.newPayPeriod];
    }
    this.policy.paySchedule = this.selectedPaySchedule;

    const success = await this.submitStep(['paySchedule', 'weekStartDay']);

    if (success) {
      this.isCreatingNewPaySchedule = false;
      this.selectedPaySchedule = this.policy.paySchedule;
    } else {
      if (this.isCreatingNewPaySchedule) {
        this.selectedPaySchedule.payPeriods = [];
      }
      this.policy.paySchedule = null;
    }

    return success;
  }

  async configurePolicy() {
    this.policy.configuringPolicy = true;
    const modelUpdated = await this.submitStep([
      'configuredAt',
      'configuringPolicy',
      'sendApprovedTimesheetEditedNotification',
      'sendPayPeriodEndedNotification',
      'sendEmployeeTimesheetReminder',
      'sendSchedulePublishedNotification'
    ]);

    if (modelUpdated) window.location = '/time_tracking/policies';
  }

  get displayPayPeriodDetails() {
    if (!this.selectedPaySchedule) return false;
    if (this.isCreatingNewPaySchedule) {
      return this.newPayPeriod && this.newPayPeriod.startDate && this.newPayPeriod.endDate && this.selectedPaySchedule.frequency;
    }
    return true;
  }

  get latestPayPeriod() {
    if (!this.selectedPaySchedule) return null;
    if (this.isCreatingNewPaySchedule) {
      return this.newPayPeriod;
    }
    return this.selectedPaySchedule.currentPayPeriod || this.selectedPaySchedule.latestPayPeriod;
  }

  get nextPayPeriod() {
    if (!this.selectedPaySchedule) return null;
    if (this.isCreatingNewPaySchedule) return this.calculatedPayPeriod;

    const {nextStartDate, nextEndDate} = this.latestPayPeriod || {};
    return nextStartDate && nextEndDate ? new TimeTrackingPayPeriod({startDate: nextStartDate, endDate: nextEndDate}) : null;
  }
}

export default PolicyEditFlowState;
